import React from "react";
import { UserContext, useEntitlement } from "../contexts";
import { useActiveStoreIdM } from "../core/hooks/useActiveStoreIdM.js";
import { useCoreListActions } from "../stock/hooks/useCoreListActions.js";

/**
 * Small red badge on the Stock Alert nav entry: the TOTAL of pending actions
 * across Core List, Ad List and Sales Pipeline (2026-11-02) — the same number
 * the three tab pills add up to, because both read /api/core-list/actions.
 *
 * It used to count /api/core-list/summary (Core List references needing
 * restocking), which knew nothing about the other two lists, live auctions or
 * "mark as done". The summary endpoint and <CoreListBanner /> still share
 * their own cache key and are untouched.
 *
 * Polls every 60s in counts-only mode — it sits on every page, so it asks for
 * the totals without the rows.
 *
 * Renders nothing for non-admins or when there is nothing to do.
 */
const CoreListNavBadge = () => {
  const { user } = React.useContext(UserContext) || {};
  const isAdmin = !!user?.is_admin;
  // The store SELECTED in the switcher, not the user's home store — this
  // badge sits in the nav on every page, so reading the wrong one meant the
  // Stock count described a store you were not looking at.
  const storeIdM = useActiveStoreIdM();
  // Core inventory management (Feature 14): this badge polls on EVERY page,
  // so without the plan it must stop asking entirely — otherwise a gated
  // store would hit a 403 app-wide, once a minute.
  const hasCoreList = useEntitlement("core_inventory_management");

  const { total } = useCoreListActions(storeIdM, {
    enabled: isAdmin && hasCoreList,
    countsOnly: true,
    refetchInterval: 60_000,
  });

  if (!isAdmin || !hasCoreList || total <= 0) return null;

  return (
    <span
      className="rc-sidebar__badge"
      aria-label={`${total} pending stock action${total === 1 ? "" : "s"}`}
    >
      {total > 99 ? "99+" : total}
    </span>
  );
};

export default CoreListNavBadge;
